/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpServersSetToolMetadataBatch } from "../funcs/mcpServersSetToolMetadataBatch.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SetToolMetadataBatchResult } from "../models/components/settoolmetadatabatchresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetMcpServerToolMetadataBatchRequest,
  SetMcpServerToolMetadataBatchSecurity,
} from "../models/operations/setmcpservertoolmetadatabatch.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetMcpServerToolMetadataBatchMutationVariables = {
  request: SetMcpServerToolMetadataBatchRequest;
  security?: SetMcpServerToolMetadataBatchSecurity | undefined;
  options?: RequestOptions;
};

export type SetMcpServerToolMetadataBatchMutationData =
  SetToolMetadataBatchResult;

export type SetMcpServerToolMetadataBatchMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setToolMetadataBatch mcpServers
 *
 * @remarks
 * Authoritative batch upsert of tool metadata for an MCP server. Every tool in the payload is upserted and any stored tool absent from the payload is soft-deleted, all in one transaction.
 */
export function useSetMcpServerToolMetadataBatchMutation(
  options?: MutationHookOptions<
    SetMcpServerToolMetadataBatchMutationData,
    SetMcpServerToolMetadataBatchMutationError,
    SetMcpServerToolMetadataBatchMutationVariables
  >,
): UseMutationResult<
  SetMcpServerToolMetadataBatchMutationData,
  SetMcpServerToolMetadataBatchMutationError,
  SetMcpServerToolMetadataBatchMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetMcpServerToolMetadataBatchMutation(client, options),
    ...options,
  });
}

export function mutationKeySetMcpServerToolMetadataBatch(): MutationKey {
  return ["@gram/client", "mcpServers", "setToolMetadataBatch"];
}

export function buildSetMcpServerToolMetadataBatchMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetMcpServerToolMetadataBatchMutationVariables,
  ) => Promise<SetMcpServerToolMetadataBatchMutationData>;
} {
  return {
    mutationKey: mutationKeySetMcpServerToolMetadataBatch(),
    mutationFn: function setMcpServerToolMetadataBatchMutationFn({
      request,
      security,
      options,
    }): Promise<SetMcpServerToolMetadataBatchMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpServersSetToolMetadataBatch(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
